/**
 * HazeSkillEffect — Poison tree corrode-path active: stationary toxic fog.
 *
 * Places a large stationary `haze` zone at a targeted location. The cloud
 * persists for 12 seconds (base), applying `poison` DoT at amplified intensity
 * (base 5 per tick, higher than Corrode's 3) to every enemy inside at 1000ms
 * intervals. Enemies inside also move 30% slower (speedMultiplier: 0.7).
 *
 * Zone geometry: circle, 3 tiles radius (stationary, wider than Miasma's drift).
 * The zone spawns at the targeted position — not the player's position.
 *
 * Seeping (haze_upgrade_1): each update tick, _checkSeeping() reads
 * entityEntryTimes on all active haze zones. If an entity has been inside for
 * ≥ seepingThresholdMs (5000ms) and its corrosion stack count is below maxStacks,
 * applyEntityStatus('corrosion', { durationMs: -1, intensity: armorReductionPerStack })
 * is called. The corrosion debuff is permanent (durationMs: -1). A unique key
 * `${zoneId}:${entityId}:${entryTime}` prevents double-applying for the same
 * continuous exposure; re-entry (new entryTime) produces a new key.
 *
 * Choking (haze_upgrade_2): post-exposure slow — wired at zone creation.
 * When postExposureSlowAmount > 0 (Choking unlocked), an `onEntityExit`
 * callback is embedded into each new haze ZoneConfig. On exit, if the entity
 * dwelled for ≥ chokingThresholdMs (3000ms), `applySlowEffect` is called with
 * intensity: postExposureSlowAmount and durationMs: postExposureSlowDurationMs.
 * ZoneSystem passes dwellMs to the callback, computed before clearing entry time.
 *
 * Dead Air (haze_passive): suppressesSound queried at activation time.
 *
 * Intensity inherits Potent Compound (scope: 'taint') and Toxicologist multiplier.
 *
 * Cooldown tier: long (2000ms).
 *
 * Pure logic — no Phaser deps.
 *
 * @see ADR-064 (attunement shared systems architecture)
 * @see poison-spec.md § Haze, Seeping
 */

import type { SkillEffect } from '../../SkillEffect.js';
import type { ZoneConfig, ActiveZone } from '../../../systems/ZoneSystem.js';

// ---------------------------------------------------------------------------
// Dependency interfaces
// ---------------------------------------------------------------------------

export interface HazeDeps {
  readonly zoneSystem: {
    create(config: ZoneConfig): string;
    getZone(id: string): ActiveZone | undefined;
    getEntityDwellTimeMs(zoneId: string, entityId: string): number;
  };
  readonly resolver: {
    getAdditiveEffect(skillId: string, key: string): number;
    getMaxEffect(skillId: string, key: string): number;
    getTreePassiveEffect(
      treeName: string,
      key: string,
      aggregation: 'sum' | 'product' | 'max' | 'min',
    ): number;
  };
  readonly targeting: {
    /** Returns the world-space position where the player is targeting. */
    getTargetPosition(): { x: number; y: number };
  };
  /** Required for Seeping: apply a permanent armor corrosion stack. */
  readonly applyEntityStatus?: (
    entityId: string,
    defId: string,
    opts: { sourceSkillId: string; durationMs: number; intensity: number },
  ) => void;
  /** Required for Seeping: read current corrosion stack count for cap check. */
  readonly getEntityEffectStacks?: (entityId: string, defId: string) => number;
  /** Required for Choking: apply post-exposure slow when entity exits after ≥ threshold. */
  readonly applySlowEffect?: (
    entityId: string,
    opts: { sourceSkillId: string; durationMs: number; intensity: number },
  ) => void;
}

// ---------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------

export interface HazeConfig {
  readonly cooldownMs: number;          // long tier = 2000ms
  readonly baseRadiusTiles: number;     // 3 tiles
  readonly baseDurationMs: number;      // 12000ms
  readonly baseIntensity: number;       // 5 (amplified vs Corrode's 3)
  readonly speedMultiplier: number;     // 0.7 (30% slow)
  readonly seepingThresholdMs: number;  // 5000ms — dwell required for Seeping
  readonly chokingThresholdMs: number;  // 3000ms — minimum exposure for post-exit slow
}

const DEFAULT_CONFIG: HazeConfig = {
  cooldownMs: 2000,
  baseRadiusTiles: 3,
  baseDurationMs: 12000,
  baseIntensity: 5,
  speedMultiplier: 0.7,
  seepingThresholdMs: 5000,
  chokingThresholdMs: 3000,
};

// ---------------------------------------------------------------------------
// Skill effect
// ---------------------------------------------------------------------------

export class HazeSkillEffect implements SkillEffect {
  readonly skillId = 'haze';
  readonly type = 'active' as const;
  readonly activationMode = 'instant' as const;

  private readonly deps: HazeDeps;
  private readonly config: HazeConfig;

  private _cooldownRemaining = 0;
  private _activationsCount = 0;
  private _zoneIds: string[] = [];

  /**
   * Tracks which (zoneId, entityId, entryTime) triples have already triggered
   * a Seeping corrosion stack. Key format: `${zoneId}:${entityId}:${entryTime}`.
   * Using entryTime in the key ensures re-entry (new timestamp) fires again.
   */
  private readonly _seepingApplied = new Set<string>();

  constructor(deps: HazeDeps, config: Partial<HazeConfig> = {}) {
    this.deps = deps;
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  // --- State getters ---

  get cooldown(): number { return this._cooldownRemaining; }
  get activationsCount(): number { return this._activationsCount; }
  get zoneIds(): readonly string[] { return this._zoneIds; }

  // --- SkillEffect interface ---

  canActivate(): boolean {
    return this._cooldownRemaining <= 0;
  }

  activate(): boolean {
    if (!this.canActivate()) return false;

    const targetPos = this.deps.targeting.getTargetPosition();
    const intensity = this.resolveIntensity();
    const durationMs = this.config.baseDurationMs +
      this.deps.resolver.getAdditiveEffect('haze', 'cloudDurationMs');

    // Dead Air passive: suppress sound detection when unlocked
    const suppressesSound =
      this.deps.resolver.getTreePassiveEffect('poison', 'suppressesSound', 'max') > 0;

    // Choking: post-exposure slow on exit if entity was inside for ≥ threshold
    const slowAmount = this.deps.resolver.getAdditiveEffect('haze', 'postExposureSlowAmount');
    const onEntityExit =
      slowAmount > 0 && this.deps.applySlowEffect
        ? this._makeChokingExitCallback(slowAmount)
        : undefined;

    const zoneId = `haze_cloud_${this._activationsCount}`;

    this.deps.zoneSystem.create({
      id: zoneId,
      type: 'haze',
      position: { x: targetPos.x, y: targetPos.y },
      durationMs,
      sourceSkillId: 'haze',
      shape: 'circle',
      radiusTiles: this.config.baseRadiusTiles,
      dotDefId: 'poison',
      dotIntensity: intensity,
      tickIntervalMs: 1000,
      speedMultiplier: this.config.speedMultiplier,
      suppressesSound: suppressesSound || undefined,
      onEntityExit,
    });

    this._zoneIds.push(zoneId);
    this._cooldownRemaining = this.config.cooldownMs;
    this._activationsCount++;

    return true;
  }

  deactivate(): void {
    // Instant — no toggle
  }

  isActive(): boolean {
    return false;
  }

  update(delta: number): void {
    if (this._cooldownRemaining > 0) {
      this._cooldownRemaining = Math.max(0, this._cooldownRemaining - delta);
    }
    this._checkSeeping();
  }

  destroy(): void {
    this._cooldownRemaining = 0;
  }

  // ---------------------------------------------------------------------------
  // Private helpers
  // ---------------------------------------------------------------------------

  /**
   * Seeping: for each active haze zone, check entityEntryTimes.
   * If any entity has been inside for ≥ seepingThresholdMs and hasn't yet
   * received a corrosion stack for this entry instance, apply one.
   * No-ops if applyEntityStatus / getEntityEffectStacks are not injected
   * (Seeping not unlocked) or if armorReductionPerStack is 0.
   */
  private _checkSeeping(): void {
    if (!this.deps.applyEntityStatus || !this.deps.getEntityEffectStacks) return;

    const armorReduction = this.deps.resolver.getAdditiveEffect('haze', 'armorReductionPerStack');
    if (armorReduction <= 0) return;

    const maxStacks = this.deps.resolver.getMaxEffect('haze', 'maxStacks') || 3;

    for (const zoneId of this._zoneIds) {
      const zone = this.deps.zoneSystem.getZone(zoneId);
      if (!zone) continue;

      for (const [entityId, entryTime] of zone.entityEntryTimes) {
        const dwellMs = this.deps.zoneSystem.getEntityDwellTimeMs(zoneId, entityId);
        const key = `${zoneId}:${entityId}:${entryTime}`;

        if (dwellMs >= this.config.seepingThresholdMs && !this._seepingApplied.has(key)) {
          const currentStacks = this.deps.getEntityEffectStacks(entityId, 'corrosion');
          if (currentStacks < maxStacks) {
            this.deps.applyEntityStatus(entityId, 'corrosion', {
              sourceSkillId: 'haze',
              durationMs: -1,
              intensity: armorReduction,
            });
          }
          // Mark fired regardless of stack cap — prevents re-firing each frame.
          this._seepingApplied.add(key);
        }
      }
    }
  }

  /**
   * Choking: build the onEntityExit callback embedded in each haze zone config.
   * Captures slowAmount and resolves slowDurationMs at creation time (not at exit
   * time) so the callback is a pure closure over stable values.
   *
   * The callback receives dwellMs from ZoneSystem (computed before entry-time
   * deletion). If dwell ≥ chokingThresholdMs, applies a `slow` status effect.
   */
  private _makeChokingExitCallback(
    slowAmount: number,
  ): (entityId: string, _zone: import('../../../systems/ZoneSystem.js').ActiveZone, dwellMs: number, _exitX: number, _exitY: number) => void {
    const slowDurationMs = this.deps.resolver.getAdditiveEffect('haze', 'postExposureSlowDurationMs');
    const thresholdMs = this.config.chokingThresholdMs;
    const applySlowEffect = this.deps.applySlowEffect!;

    return (entityId: string, _zone, dwellMs: number) => {
      if (dwellMs >= thresholdMs) {
        applySlowEffect(entityId, {
          sourceSkillId: 'haze',
          durationMs: slowDurationMs,
          intensity: slowAmount,
        });
      }
    };
  }

  private resolveIntensity(): number {
    const base = this.config.baseIntensity;
    // Inherit Potent Compound from the shared trunk (scope: 'taint')
    const bonus = this.deps.resolver.getAdditiveEffect('taint', 'dotDamagePerTick');
    const multiplier = this.deps.resolver.getTreePassiveEffect(
      'poison', 'dotDamageMultiplier', 'product',
    );
    return (base + bonus) * multiplier;
  }
}
